import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Pagination,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  LocationOn,
  Refresh,
  CellTower,
  Map,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import spopService from '../services/spopService';
import { useAuth } from '../hooks/useAuth';
import { SPOP } from '../types';

const SPOPs: React.FC = () => {
  const [spops, setSPOPs] = useState<SPOP[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [stats, setStats] = useState<any>(null);
  const { user } = useAuth();
  
  const navigate = useNavigate();
  const limit = 10;

  // Get SPOP filter for operators
  const spopFilter = user?.role === 'operator' && user?.spop_id ? user.spop_id : undefined;

  const fetchSPOPs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const isActiveFilter = statusFilter === 'all' ? undefined : statusFilter === 'active';
      
      // For operators, apply SPOP filtering
      const filters: any = {};
      if (spopFilter) {
        filters.spop_id = spopFilter;
      }
      
      const response = await spopService.getSPOPs(page, limit, search, isActiveFilter, filters);
      
      if (response.data?.success) {
        setSPOPs(response.data.data.spops);
        setTotalPages(response.data.data.pagination.totalPages);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Gagal memuat data SPOP');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // For operators, apply SPOP filtering to stats as well
      const filters = spopFilter ? { spop_id: spopFilter } : undefined;
      const response = await spopService.getSPOPStats(filters);
      if (response.data?.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  useEffect(() => {
    fetchSPOPs();
  }, [page, search, statusFilter]);

  useEffect(() => {
    fetchStats();
  }, []);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handleStatusFilter = (event: any) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus SPOP ini?')) {
      try {
        await spopService.deleteSPOP(id);
        fetchSPOPs();
        fetchStats();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Gagal menghapus SPOP');
      }
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const openInMaps = (latitude: number, longitude: number) => {
    const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
    window.open(url, '_blank');
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Data SPOP
          </Typography>
          {user?.role === 'operator' && user?.spop && (
            <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
              SPOP: {user.spop.name} ({user.spop.areaCode})
            </Typography>
          )}
        </Box>
        {user?.role === 'admin' && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/master-data/spop/create')}
          >
            Tambah SPOP
          </Button>
        )}
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Stack direction="row" spacing={2} mb={3}>
          <Card sx={{ flex: 1, bgcolor: 'primary.50' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <CellTower sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" color="primary.main" fontWeight="bold">
                {stats.total}
              </Typography>
              <Typography variant="subtitle2">Total SPOP</Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ flex: 1, bgcolor: 'success.50' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <LocationOn sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {stats.active}
              </Typography>
              <Typography variant="subtitle2">Aktif</Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ flex: 1, bgcolor: 'error.50' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <LocationOn sx={{ fontSize: 32, color: 'error.main', mb: 1 }} />
              <Typography variant="h4" color="error.main" fontWeight="bold">
                {stats.inactive}
              </Typography>
              <Typography variant="subtitle2">Tidak Aktif</Typography>
            </CardContent>
          </Card>
        </Stack>
      )}

      {/* Search and Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              placeholder="Cari SPOP..."
              value={search}
              onChange={handleSearch}
              size="small"
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilter}
              >
                <MenuItem value="all">Semua</MenuItem>
                <MenuItem value="active">Aktif</MenuItem>
                <MenuItem value="inactive">Tidak Aktif</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                fetchSPOPs();
                fetchStats();
              }}
            >
              Refresh
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* SPOP Table */}
      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Nama SPOP</strong></TableCell>
                <TableCell><strong>Area Code</strong></TableCell>
                <TableCell><strong>Alamat</strong></TableCell>
                <TableCell><strong>Radius (m)</strong></TableCell>
                <TableCell><strong>Koordinat</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Aksi</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : spops.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Tidak ada data SPOP
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                spops.map((spop) => (
                  <TableRow key={spop.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {spop.name}
                      </Typography>
                      {spop.description && (
                        <Typography variant="caption" color="text.secondary">
                          {spop.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={spop.areaCode}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200 }}>
                        {spop.address}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {typeof spop.radius === 'number' ? spop.radius.toLocaleString() : parseInt(spop.radius || '0').toLocaleString()} m
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Typography variant="caption" fontFamily="monospace">
                          {typeof spop.latitude === 'number' ? spop.latitude.toFixed(6) : parseFloat(spop.latitude || '0').toFixed(6)}, {typeof spop.longitude === 'number' ? spop.longitude.toFixed(6) : parseFloat(spop.longitude || '0').toFixed(6)}
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<Map />}
                          onClick={() => openInMaps(
                            typeof spop.latitude === 'number' ? spop.latitude : parseFloat(spop.latitude || '0'),
                            typeof spop.longitude === 'number' ? spop.longitude : parseFloat(spop.longitude || '0')
                          )}
                        >
                          Lihat di Maps
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={spop.isActive ? 'Aktif' : 'Tidak Aktif'}
                        color={spop.isActive ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {user?.role === 'admin' && (
                          <>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => navigate(`/master-data/spop/edit/${spop.id}`)}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(spop.id)}
                            >
                              <Delete />
                            </IconButton>
                          </>
                        )}
                        {user?.role === 'operator' && (
                          <Typography variant="body2" color="text.secondary">
                            Hanya dapat melihat
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" p={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default SPOPs;
